/**
 * email_send — Send email via River (Mailgun) from any AI context.
 * Description and schema derived from canonical schema file in MailApp.
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { API_BASE_URL } from '../../../../api/config';
import fetchService from '../../../../services/fetchService';
import { schemaToParameters } from '../../../../utils/schemaToParameters';

export const emailSendCommand: SelfContainedCommand = {
  id: 'email_send',
  description: `Send an email via River. Sends from River's managed email address (Mailgun). Use for notifications, daily briefings, summaries, and automated outreach.

**RECIPIENT FORMAT:**
- Single: "alice@example.com"
- Named: "Alice Johnson <alice@example.com>"
- Multiple: "alice@example.com, bob@example.com"

**BODY FORMAT:**
Plain text with newlines preserved. Use \\n for line breaks. HTML is supported via the optional body_html field.

**EXAMPLES:**

Send a daily briefing:
{ "to": "user@example.com", "subject": "Your Daily Briefing - Monday Feb 28", "body": "Good morning!\\n\\nHere are your top priorities for today:\\n\\n1. Review contract renewal (HIGH)\\n2. Team standup at 10am\\n3. Send Q1 report" }

Send a notification:
{ "to": "alice@acme.com", "subject": "Task Completed: Q1 Analysis", "body": "Hi Alice,\\n\\nThe Q1 analysis has been completed and saved to your workspace.\\n\\nBest,\\nRiver" }

Send with custom sender name:
{ "to": "team@company.com", "subject": "Weekly Summary", "body": "See attached weekly summary.", "from_name": "River Assistant" }`,
  autonomyLevel: 'confirm',
  visualTreatment: 'narrative',

  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        to: {
          type: 'string',
          description: 'Recipient email address(es), comma-separated. Supports "Name <email>" format.',
        },
        subject: {
          type: 'string',
          description: 'Email subject line',
        },
        body: {
          type: 'string',
          description: 'Email body as plain text. Use \\n for line breaks.',
        },
        body_html: {
          type: 'string',
          description: 'Optional HTML version of the body. If omitted, plain text body is used.',
        },
        from_name: {
          type: 'string',
          description: 'Display name for the sender (default: "River")',
          default: 'River',
        },
      },
      required: ['to', 'subject', 'body'],
    }),
  },

  examples: {
    good: [
      {
        input: {
          to: 'user@example.com',
          subject: 'Daily Briefing — Feb 26',
          body: 'Here are today\'s top AI news stories:\n\n1. ...\n2. ...\n3. ...\n\n— River AI',
        },
        expectedOutput: createSuccessResult('Email sent to user@example.com', { messageId: 'abc' }),
        description: 'Send a daily briefing email',
      },
    ],
    bad: [
      {
        input: { to: 'not-an-email', subject: 'Test', body: 'hi' },
        error: 'Invalid email address',
        why: 'The to field must be a valid email',
        fix: 'Use a valid email address like user@example.com',
      },
    ],
  },

  handler: async (
    params: { to: string; subject: string; body: string; from_name?: string },
    context: CommandExecutionContext
  ): Promise<StandardToolResult> => {
    const { to, subject, body, from_name } = params;

    try {
      const result = await fetchService(`${API_BASE_URL}/mail/send`, {
        method: 'POST',
        body: JSON.stringify({ to, subject, body, from_name }),
      });

      return createSuccessResult(
        `Email sent to ${to}`,
        { messageId: result.messageId, to, subject, via: 'mailgun' }
      );
    } catch (err: any) {
      return createErrorResult(
        'Failed to send email',
        err?.message || 'Unknown error',
        'execution'
      );
    }
  },
};
